import type { Coordinates } from 'vue-advanced-cropper';
import type { GroupImageDisplayModel, SubjectImageDisplayModel } from '@shared/models/subjects/images';
import type { PackageDisplayModel } from '@shared/models/packages/packages';
import type { Cartv2PackageItemDisplayModel } from '@shared/models/carts/cartPackages';
import { SINGLE_DIGITAL_DOWNLOAD, WHOLE_DIGITAL_DOWNLOAD } from '@shared/models/products/productLookup';
import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type { PackageItemDisplayModel } from '@shared/models/packages/packageItems';
import type { PackageItemImageNodeDisplayModel } from '@shared/models/packages/packageNodes';
import { computed } from 'vue';
import { EntrySubjectGroupImageModel } from '@shared/models/entry/entry';
import { useOrganizationEditorStore } from '@apps/organization-editor/stores/organizationEditor.store';

export interface IDimensions {
  width: number;
  height: number;
  ratio?: number;
}

export interface ICropPosition {
  cropX: number;
  cropY: number;
  xMid: number;
  yMid: number;
}

export interface ICropCoords extends IDimensions {
  left: number;
  top: number;
  bottom?: number;
  right?: number;
}

const aspectRatios = {
  vertical5x7: {
    min: 0.7,
    max: 0.73,
  },
  horizontal7x5: {
    min: 1.37,
    max: 1.43,
  },
  horizontal10x8: {
    min: 1.23,
    max: 1.28,
  },
};

const isGroupImageFn = (
  image: EntrySubjectGroupImageModel | GroupImageDisplayModel,
): image is GroupImageDisplayModel => {
  return (image as GroupImageDisplayModel).groupId !== undefined;
};

export const useImageData = (image: EntrySubjectGroupImageModel | GroupImageDisplayModel) => {
  if (!image) {
    console.warn('No image provided to useImageData');
  }

  const isIndividualImage = computed(() => !isGroupImageFn(image) && image.imageLookup?.type === 'hosted-individual');

  const isGroupImage = computed(() => isGroupImageFn(image) || image.imageLookup?.type === 'hosted-group');

  const isLandscape = computed(() => {
    if (image) {
      const { width, height } = image;
      if (!width || !height) return false;
      return width > height;
    }
  });

  const isPortrait = computed(() => {
    if (image) {
      const { width, height } = image;
      if (!width || !height) return false;
      return width < height;
    }
  });

  const isInVertical5x7Range = computed(() => {
    if (image) {
      const { width, height } = image;
      if (!width || !height) return false;

      const aspectRatio = width / height;
      return aspectRatio >= aspectRatios.vertical5x7.min && aspectRatio <= aspectRatios.vertical5x7.max;
    }
  });

  const isInHorizontal7x5Range = computed(() => {
    if (image) {
      const { width, height } = image;
      if (!width || !height) return false;

      const aspectRatio = width / height;
      return aspectRatio >= aspectRatios.horizontal7x5.min && aspectRatio <= aspectRatios.horizontal7x5.max;
    }
  });

  const isInHorizontal10x8Range = computed(() => {
    if (image) {
      const { width, height } = image;
      if (!width || !height) return false;

      const aspectRatio = width / height;
      return aspectRatio >= aspectRatios.horizontal10x8.min && aspectRatio <= aspectRatios.horizontal10x8.max;
    }
  });

  return {
    isIndividualImage,
    isGroupImage,
    isLandscape,
    isPortrait,
    isInVertical5x7Range,
    isInHorizontal7x5Range,
    isInHorizontal10x8Range,
  };
};

export const useComputedAspectRatio = (image: EntrySubjectGroupImageModel) => {
  const {
    isLandscape,
    isPortrait,
    isInVertical5x7Range,
    isInHorizontal7x5Range,
    isInHorizontal10x8Range,
    isIndividualImage,
    isGroupImage,
  } = useImageData(image);

  const organizationStore = useOrganizationEditorStore();
  const isSportsOrganization = computed(() => organizationStore.isSportsOrganization);

  const aspectRatio = computed(() => {
    if (isIndividualImage.value && isPortrait.value) {
      if (isInVertical5x7Range.value && isSportsOrganization.value) {
        return 5 / 7;
      } else {
        return 8 / 10;
      }
    } else if (isIndividualImage.value && isLandscape.value) {
      if (isInHorizontal7x5Range.value && isSportsOrganization.value) {
        return 7 / 5;
      } else {
        return 10 / 8;
      }
    } else if (isGroupImage.value && isPortrait.value) {
      if (isInVertical5x7Range.value) {
        return 5 / 7;
      } else {
        return 8 / 10;
      }
    } else if (isGroupImage.value && isLandscape.value) {
      if (isInHorizontal10x8Range.value) {
        return 10 / 8;
      } else {
        return 7 / 5;
      }
    } else {
      console.warn('Unknown aspect ratio, defaulting to 8/10');
      return 8 / 10; // Default, shouldn't reach here
    }
  });

  return aspectRatio.value;
};

export const loadImage = async (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.src = src;
  });

export const getImageMetaData = async (imageThumbnailUrl: string): Promise<IDimensions> => {
  const newImg = await loadImage(imageThumbnailUrl);
  return {
    height: newImg.height,
    width: newImg.width,
  };
};

export const getCropDataFromSoftCropImageUsingImage = async (image: SubjectImageDisplayModel): Promise<ICropCoords> => {
  const cropPosition: ICropPosition = {
    cropX: image.cropX,
    cropY: image.cropY,
    xMid: image.xMid,
    yMid: image.yMid,
  };

  return await getCropDataFromCropPosition(cropPosition, image.imageUrl);
};

export const getCropDataFromSoftCropUsingThumbnail = async (image: SubjectImageDisplayModel): Promise<ICropCoords> => {
  const cropPosition: ICropPosition = {
    cropX: image.cropX,
    cropY: image.cropY,
    xMid: image.xMid,
    yMid: image.yMid,
  };

  return await getCropDataFromCropPosition(cropPosition, image.imageThumbnailUrl);
};

export const getCropDataFromCropPosition = async (o: ICropPosition, src: string): Promise<ICropCoords> => {
  const { cropX, cropY, xMid, yMid } = o;
  const { width: imageWidth, height: imageHeight } = await loadImage(src);

  const cropWidth = imageWidth * (cropX / 100);
  const cropHeight = imageHeight * (cropY / 100);

  const cropCenterX = imageWidth * (xMid / 100);
  const cropCenterY = imageHeight * (yMid / 100);

  const cropLeft = cropCenterX - cropWidth / 2;
  const cropTop = cropCenterY - cropHeight / 2;

  return {
    left: cropLeft,
    top: cropTop,
    width: cropWidth,
    height: cropHeight,
  };
};

export const convertCropToSoftCropPercentages = (coordinates: Coordinates, image: IDimensions) => {
  const cropX = Math.round((coordinates.width / image.width) * 100);
  const cropY = Math.round((coordinates.height / image.height) * 100);
  const yMid = Math.round(((coordinates.top + coordinates.height / 2) / image.height) * 100);
  const xMid = Math.round(((coordinates.left + coordinates.width / 2) / image.width) * 100);

  return {
    cropX,
    cropY,
    xMid,
    yMid,
  };
};

export const getAspectRatio = (dims: IDimensions) => {
  const { height, width } = dims;
  return {
    height,
    width,
    ratio: height / width,
  };
};

export const allowCrop = (
  pkg: PackageDisplayModel,
  item: PackageItemDisplayModel | Cartv2PackageItemDisplayModel,
): boolean =>
  pkg.allowConsumerCrop && item.productCode !== WHOLE_DIGITAL_DOWNLOAD && item.productCode !== SINGLE_DIGITAL_DOWNLOAD;

/**
 * Get the coordinates from the product catalog format or the DP2 format
 * @param coords
 */
export const convertCoordinates = (coords: string): ICropCoords => {
  if (coords.includes('~')) {
    return convertDp2Coordinates(coords);
  }
  return convertProductCatalogCoordinates(coords);
};
/**
 * Get the coordinates from the product catalog format
 * LeftSideOfNode TopOfNode WidthOfNode HeightOfNode
 * @param coords
 */
export const convertProductCatalogCoordinates = (coords: string): ICropCoords => {
  const splitCoords = coords.split(' ').map((m) => Number.parseFloat(m));

  return {
    left: splitCoords[0],
    top: splitCoords[1],
    width: splitCoords[2],
    height: splitCoords[3],
  };
};

/**
 * Get the coordinates from the DP2 format
 * LeftSideOfNode TopOfNode BottomOfNode RightSideOfNode WidthOfNode HeightOfNode
 * @param dp2Coords
 */
export const convertDp2Coordinates = (dp2Coords: string): ICropCoords => {
  const splitCoords = dp2Coords.split('~').map((m) => Number.parseFloat(m));

  return {
    height: splitCoords[5],
    width: splitCoords[4],
    left: splitCoords[0],
    top: splitCoords[1],
    bottom: splitCoords[2],
    right: splitCoords[3],
  };
};

export const isNodeFlipped = (
  cartNode: CartPackageImageNodeDisplayModel,
  itemNode: PackageItemImageNodeDisplayModel,
  softCrop?: ICropCoords,
) => {
  // if the node has frontendCropDimensions, it's been cropped so we can't use the DP2 coordinates to determine flipped status
  // therefore we get the width/height of the crop dimensions and check if the crop has been rotated by
  // first checking the difference between the unflipped ratio and the original ratio, and comparing that to the flipped ratio difference.
  // we use the closest result.  Since the ratio can change slightly based on image size and other variables
  const { height: originalHeight, width: originalWidth } = convertCoordinates(itemNode.positionCoords);
  const originalRatio = originalWidth / originalHeight;
  const crop = cartNode.frontendCropDimensions || softCrop;
  if (crop) {
    const { width: cropWidth, height: cropHeight } = crop;
    const unflippedDiff = Math.abs(originalRatio - cropWidth / cropHeight);
    const flippedDiff = Math.abs(originalRatio - cropHeight / cropWidth);
    return unflippedDiff > flippedDiff;
  }

  return false;
};

export const aspectRatioFromCartNode = (node: CartPackageImageNodeDisplayModel, invert: boolean) => {
  const { height, width } = node.frontendCropDimensions || { width: 1, height: 1 };
  return invert ? height / width : width / height;
};
export const aspectRatioFromPackageNode = (node: PackageItemImageNodeDisplayModel, invert: boolean) => {
  const { height, width } = convertCoordinates(node.positionCoords);
  return invert ? height / width : width / height;
};

export const maintainCropCenterFromCoords = (coords: ICropCoords) =>
  maintainCropCenter(coords.top, coords.left, coords.width, coords.height);

export const maintainCropCenter = (top: number, left: number, width: number, height: number) => {
  const center = {
    left: left + width / 2,
    top: top + height / 2,
  };
  return ({ coordinates }: { coordinates: ICropCoords }) => ({
    left: center.left - coordinates.width / 2,
    top: center.top - coordinates.height / 2,
  });
};

export const getOrderXmlCropDimensions = (
  cropCoordinates: ICropCoords,
  imageMetaData: { width: number; height: number },
  normalizedRotation: NormalizedRotation,
) => {
  let imgWidth = imageMetaData.width;
  let imgHeight = imageMetaData.height;

  // Swap width and height if rotated by 90 or 270 degrees
  if (normalizedRotation === 90 || normalizedRotation === 270) {
    imgWidth = imageMetaData.height;
    imgHeight = imageMetaData.width;
  }

  const newCropX = ((cropCoordinates.width / imgWidth) * 100).toFixed(0);
  const newCropY = ((cropCoordinates.height / imgHeight) * 100).toFixed(0);
  const newXMid = (((cropCoordinates.left + cropCoordinates.width / 2) / imgWidth) * 100).toFixed(0);
  const newYMid = (((cropCoordinates.top + cropCoordinates.height / 2) / imgHeight) * 100).toFixed(0);

  return `${newXMid} ${newYMid} ${newCropX} ${newCropY}`;
};

export const getCropDataFromOrderCropPosition = (cropPosition: string): ICropPosition => {
  const [xMid, yMid, cropX, cropY] = cropPosition.split(' ').map((m) => Number.parseFloat(m));
  return {
    cropX,
    cropY,
    xMid,
    yMid,
  };
};

type NormalizedRotation = 0 | 90 | 180 | 270;

/**
 * Takes a rotation and normalizes it to be between 0 and 359
 * @param rotation The rotation value to normalize
 * @returns The normalized rotation value
 */
export const normalizeRotation = (rotation: number): NormalizedRotation => {
  return (((rotation % 360) + 360) % 360) as NormalizedRotation;
};
